import React, {useEffect} from 'react'

import dynamic from 'next/dynamic'
import styled from 'styled-components'
import {useHits} from 'react-instantsearch'
import {RiIndeterminateCircleLine} from '@remixicon/react'

import {VariantList} from '@festi/common/api/rest'
import {EmptyResults} from '@festi/common/components/common'
import {useUserPrices} from '@festi/common/utils/rest'

import NewProductCard from 'src/components/cards/redesign/NewProductCard'
import ProductCardLoader from 'src/components/cards/redesign/utilities/ProductCardLoader'
import {EventIndexQueryID} from '../utilites/algoliaEvents'
import {useAlgoliaContext} from '../context/AlgoliaContext'

const PageFilters = dynamic(() => import('./PageFilters'), {
  ssr: false,
})

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  grid-gap: 16px;
  padding-top: 24px;

  &.small {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  }

  &.list {
    display: flex;
    flex-direction: column;
  }
`

interface HitsProps {
  attrNameMap?: Record<string, string>
}

export default function CustomHits({attrNameMap}: HitsProps) {
  const hits = useHits<VariantList>()

  const {gridType, setNumberOfHits} = useAlgoliaContext()

  const variants = hits?.items

  const {data: userPrices} = useUserPrices(variants?.map((v) => v.sku))

  const facetsOrder =
    hits?.results?.renderingContent?.facetOrdering?.facets?.order

  useEffect(() => {
    setNumberOfHits?.(hits?.results?.nbHits || 0)
  }, [hits?.results?.nbHits, setNumberOfHits])

  return (
    <>
      <PageFilters facets={facetsOrder} attrNameMap={attrNameMap} />

      {!!variants?.length ? (
        <ProductGrid className={gridType}>
          {variants.map((variant) => (
            <NewProductCard
              key={variant.id}
              origin={variant.__queryID}
              variant={variant}
              position={variant.__position}
              gridType={gridType}
              image={{
                url: variant.firstImage.image.webpS || '',
                jpg: variant.firstImage.image.jpgS || '',
                alt: variant.firstImage.alt || variant.name || '',
              }}
              userPrice={userPrices?.[variant.sku]}
            />
          ))}
        </ProductGrid>
      ) : !hits?.results?.queryID ? (
        <ProductGrid className={gridType}>
          {[...Array(20)].map((_, i) => (
            <ProductCardLoader key={i} horizontal={gridType === 'list'} />
          ))}
        </ProductGrid>
      ) : (
        <EmptyResults icon={RiIndeterminateCircleLine}>
          Engar vörur fundust
        </EmptyResults>
      )}

      <EventIndexQueryID
        index={hits?.results?.index}
        queryID={hits?.results?.queryID}
      />
    </>
  )
}
